import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogTitle,
  MultiSelect,
} from '@ncoder/ui';
import type { OrgMember, OrgRole } from '@/services/org-admin';
import { FormField } from './OrgAdminForm';

/**
 * Move members onto one role, from that role's own card.
 *
 * WHY THIS EXISTS ALONGSIDE THE MEMBERS TAB. Both call the same `updateOrgUserRole` mutation, but
 * they serve opposite directions of the same job. The Members tab answers "what should THIS PERSON
 * have?" — one person, one dropdown, per row. This answers "who should be on THIS ROLE?" — which,
 * done from the Members tab, means finding each person in a roster and setting the same value N
 * times, with no way to see the set you have built. Neither is redundant; they are a transpose of
 * each other.
 *
 * THE ROLE IS FIXED AND THE MEMBERS VARY. That is the whole distinction from `RoleFormModal`, which
 * edits what the role GRANTS. This changes who HOLDS it and touches no role definition.
 *
 * Two constraints are enforced by the caller, not here, because they are properties of the actor and
 * the role rather than of this form: whether the actor may grant a role carrying this much authority
 * (an admin may not), and which members are eligible at all (active, and not already on the role).
 * See `assignableMembers.ts`.
 */

export interface AssignRoleMembersDialogProps {
  open: boolean;
  /** `null` while closed, so the dialog never renders a stale role's name mid-transition. */
  role: OrgRole | null;
  /** Already filtered to eligible members by the caller. */
  candidates: OrgMember[];
  isSubmitting: boolean;
  /**
   * Progress across a multi-member assignment.
   *
   * There is no bulk mutation — each member is one request — so a partial failure is a real outcome
   * and the dialog stays open reporting it rather than closing on a lie. `null` before any attempt.
   */
  progress: { done: number; total: number; failed: string[] } | null;
  onSubmit: (userIds: string[]) => void;
  onClose: () => void;
}

export function AssignRoleMembersDialog({
  open,
  role,
  candidates,
  isSubmitting,
  progress,
  onSubmit,
  onClose,
}: AssignRoleMembersDialogProps) {
  const [selected, setSelected] = useState<string[]>([]);

  /*
   * Clear the selection when the dialog closes or the role changes.
   *
   * Without this, opening Role B after assigning to Role A arrives with A's people pre-ticked — and
   * the submit button would cheerfully move them onto B. The role id is the dependency, not just
   * `open`, because the panel can swap roles without an intervening close.
   */
  useEffect(() => {
    setSelected([]);
  }, [open, role?.id]);

  const failedCount = progress?.failed.length ?? 0;
  const canSubmit = !isSubmitting && selected.length > 0 && candidates.length > 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isSubmitting && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            Assign members to “{role?.name ?? 'role'}”
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            {/* Names the consequence, not the mechanism. Moving someone onto a role replaces the one
                they hold — a member has exactly one — and if this role carries admin authority that
                is a privilege change, which the admin should read before submitting rather than
                discover afterwards. */}
            Each member holds one role, so this replaces whichever they hold now.
            {role && role.management_level !== 'member' && (
              <>
                {' '}
                <span className="font-medium text-foreground">
                  This role carries {role.management_level} authority.
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogMain className="space-y-4">
          <FormField
            label="Members"
            required
            hint={
              candidates.length === 0
                ? 'Every active member already holds this role.'
                : `${selected.length} of ${candidates.length} selected. Deactivated members are not listed — reactivate them first.`
            }
          >
            {({ id, ...fieldProps }) => (
              <div id={id} {...fieldProps}>
                <MultiSelect
                  // Remounts when the role changes, so the uncontrolled MultiSelect does not keep
                  // the previous role's ticks. Same reason as `RoleFormModal`'s key.
                  key={role?.id ?? 'none'}
                  options={candidates.map((member) => ({
                    label: member.email,
                    value: member.user_id,
                  }))}
                  onValueChange={setSelected}
                  defaultValue={[]}
                  placeholder={
                    candidates.length === 0 ? 'Nobody to assign' : 'Select members…'
                  }
                  searchable
                  modalPopover
                  disabled={isSubmitting || candidates.length === 0}
                />
              </div>
            )}
          </FormField>

          {/*
            Progress and partial failure, reported per member.
            A summary of "4 assigned" when one of five failed is the failure mode this replaces: the
            admin closes the dialog believing the set is complete.
          */}
          {progress && (
            <div
              role="status"
              className="rounded-[var(--oa-radius-md)] border border-border/70 bg-muted/30 p-3"
            >
              <p className="text-[13px] font-medium">
                {progress.done} of {progress.total} assigned
                {failedCount > 0 && ` · ${failedCount} failed`}
              </p>
              {failedCount > 0 && (
                <ul className="oa-meta mt-1.5 space-y-0.5">
                  {progress.failed.map((email) => (
                    <li key={email} className="break-words">
                      {email}
                    </li>
                  ))}
                </ul>
              )}
              {failedCount > 0 && (
                <p className="oa-meta mt-1.5">
                  The ones listed above still hold their previous role. The rest were changed.
                </p>
              )}
            </div>
          )}
        </DialogMain>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {/* "Close", not "Cancel", once something has been assigned: cancel implies undoing, and
                the assignments that already succeeded are not undone by dismissing this. */}
            {progress ? 'Close' : 'Cancel'}
          </Button>
          <Button type="button" onClick={() => onSubmit(selected)} disabled={!canSubmit}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Assigning…
              </>
            ) : (
              <>
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                {selected.length > 1 ? `Assign ${selected.length} members` : 'Assign member'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
